import React from "react";
import {
  BrowserRouter as Router,
  Switch,
  Route,
  Redirect,
  useLocation,
} from "react-router-dom";
import Register from "./domain/Register/Register";
import Login from "./domain/Login/Login";
import ChangePassword from "./domain/ChangePassword/ChangePassword";
import AppBar from "./components/AppBar/AppBar";
import NavBar from "./components/NavBar/NavBar";
import Dashboard from "./domain/Dashboard/Dashboard";
import Status from "./components/NetworkStatus";
// import Develop from "./domain/Develop/Develop";

import { AuthProvider, useAuth } from "./context/AuthContext";
import { KioskModeProvider, useKioskMode } from "./context/KioskModeContext";
import { LocaleProvider } from "./context/LocaleContext";
import { useModuleAccess } from "./hooks/useModuleAccess";
import "./styles/App.scss";
import DeviceDetails from "./domain/Devices/DeviceDetails";
import GroupDetails from "./domain/Devices/GroupDetails";
import ProgramEditor from "./domain/Develop/Program/ProgramEditor";
import Data from "./domain/Data/Data";
import Groups from "./domain/Devices/Groups";
import Loading from "./components/Loading";
import Test from "./components/Test";
import UserProfile from "./components/Profile/UserProfile";
import SettingsMainPage from "./components/Settings/SettingsMainPage";
import Notifications from "./domain/Notifications/Notifications";
import Dataset from "./domain/Data/Dataset/Dataset";
import ActionEditor from "./domain/Develop/Action/ActionEditor";
import Models from "./domain/Data/Model/Models";
import SettingsMain from "./domain/Settings/SettingsMain";
import Model from "./domain/Data/Model/Model";
import Programs from "./domain/Develop/Program/Programs";
import Actions from "./domain/Develop/Action/Actions";
import Datasets from "./domain/Data/Dataset/Datasets";
import Workers from "./domain/Data/Workers/Workers";
import General from "./domain/Devices/Shared/General";
import JetsonNano from "./domain/Devices/JetsonNano/JetsonNano";
import Manual from "./domain/Devices/Shared/Manual";
import Program from "./domain/Devices/Shared/Program";
import Settings from "./domain/Devices/Shared/Settings";
import Control from "./domain/Devices/Delta/Tabs/Control";
import Stream from "./domain/Devices/Camera/Tabs/Stream";
import StereoStream from "./domain/Devices/StereoCamera/Tabs/Stream";
import StereoDiagnostics from "./domain/Devices/StereoCamera/Tabs/Diagnostics";
import DeviceChat from "./domain/Devices/Chat/DeviceChat";
import ChatWidget from "./domain/Devices/Chat/ChatWidget";
import Logs from "./domain/Devices/Shared/Logs";
import Develop from "./domain/Develop/Develop";
import DevelopConfig from "./domain/Config/DevelopConfig";
import Support from "./components/Support/Support";
import DataConfig from "./domain/Config/DataConfig";
import Marketplace from "./domain/Marketplace/Marketplace";
import Devices from "./domain/Marketplace/Devices/Devices";
import BlockLibraries from "./domain/Marketplace/BlockLibraries/BlockLibraries";
import Annotation from "./domain/Data/Dataset/Annotation";
import ForgotPassword from "./domain/ForgotPassword/ForgotPassword";
import VerifyTokenCode from "./domain/VerifyTokenCode/VerifyTokenCode";
import BlockLibrary from "./domain/Marketplace/BlockLibraries/BlockLibrary/BlockLibrary";
import OrganizationAdmin from "./domain/Organization/OrganizationAdmin";

// MES imports
import MES from "./domain/MES/MES";
import Planning from "./domain/MES/Planning/Planning";
import StarterPlan from "./domain/MES/Planning/StarterPlan";
import StaffAllocation from "./domain/MES/Planning/StaffAllocation";
import MESMonitoringDashboard from "./domain/MES/Monitoring/Dashboard";
import KPIDashboard from "./domain/MES/Monitoring/KPIDashboard";
import WorkOrders from "./domain/MES/Execution/WorkOrders";
import ProductionWorkOrders from "./domain/MES/Execution/ProductionWorkOrders";
import TabletWorkOrders from "./domain/MES/Execution/TabletWorkOrders";
import ProcessData from "./domain/MES/Execution/ProcessData";
import Trials from "./domain/MES/Development/Trials";
import TrialCreatePage from "./domain/MES/Development/TrialCreatePage";
import TrialDetailPage from "./domain/MES/Development/TrialDetailPage";
import SavedTrials from "./domain/MES/Development/SavedTrials";
import TrialTablet from "./domain/MES/Development/TrialTablet";
import TrialDashboard from "./domain/MES/Development/TrialDashboard";
import FlourQualityQueue from "./domain/MES/Quality/FlourQualityQueue";
import FlourQualityLotPage from "./domain/MES/Quality/FlourQualityLotPage";
import FlourQualityHistory from "./domain/MES/Quality/FlourQualityHistory";
import LabosMeasurements from "./domain/MES/Labos/LabosMeasurements";
import LabosNalozi from "./domain/MES/Labos/LabosNalozi";
import IngredientPrices from "./domain/MES/Finance/IngredientPrices";
import ProductCosts from "./domain/MES/Finance/ProductCosts";
import OrderRequests from "./domain/MES/Procurement/OrderRequests";
import Products from "./domain/MES/Database/Products";
import Recipes from "./domain/MES/Database/Recipes";
import SemiFinished from "./domain/MES/Database/SemiFinished";
import Machines from "./domain/MES/Database/Machines";
import Ingredients from "./domain/MES/Database/Ingredients";
import ReceiveDelivery from "./domain/MES/Warehouse/ReceiveDelivery";
import InterWarehouseTransfers from "./domain/MES/Warehouse/InterWarehouseTransfers";
import StockOverview from "./domain/MES/Warehouse/StockOverview";
import StockByWarehouse from "./domain/MES/Warehouse/StockByWarehouse";
import Inventura from "./domain/MES/Warehouse/Inventura";
import FinishedGoods from "./domain/MES/Warehouse/FinishedGoods";
import Traceability from "./domain/MES/Warehouse/Traceability";
import DispatchNotes from "./domain/MES/Warehouse/DispatchNotes";
import RegistryOverview from "./domain/MES/Overview/RegistryOverview";
import DailyReport from "./domain/MES/Reports/DailyReport";
import Batches from "./domain/MES/Batches/Batches";
import BatchDetail from "./domain/MES/Batches/BatchDetail";
import TabletMixer from "./domain/MES/Tablet/TabletMixer";
import TabletProcessing from "./domain/MES/Tablet/TabletProcessing";
import TabletOven from "./domain/MES/Tablet/TabletOven";
import TabletPackaging from "./domain/MES/Tablet/TabletPackaging";

function PrivateRoute({ children, path }: { children: React.ReactNode; path: string }) {
  const { isAuthenticated } = useAuth();
  if (!isAuthenticated) return <Redirect to="/login" />;
  return <Route path={path}>{children}</Route>;
}

function KioskExitButton() {
  const { disableKioskMode } = useKioskMode();
  return (
    <button
      className="kiosk-exit-button"
      onClick={disableKioskMode}
      title="Exit Kiosk Mode"
    >
      ✕
    </button>
  );
}

function MainLayout({ children }: { children: React.ReactNode }) {
  const { isKioskMode } = useKioskMode();
  const { canAccessPath, landingPath } = useModuleAccess();
  const location = useLocation();

  // Hiding the menu entry is not enough; a restricted role must not reach the
  // page by typing the URL either.
  if (!canAccessPath(location.pathname)) {
    return <Redirect to={landingPath} />;
  }

  if (isKioskMode) {
    return (
      <>
        <KioskExitButton />
        <div className="main-container kiosk-mode">
          {children}
        </div>
      </>
    );
  }

  return (
    <>
      <AppBar />
      <div className="top-container">
        <NavBar />
        <div className="main-container">
          {children}
        </div>
      </div>
      {/* App-wide floating device chat launcher (hidden in kiosk mode above) */}
      <ChatWidget />
    </>
  );
}

function PublicRoute({ component: Component, ...rest }) {
  return <Route {...rest} render={(props) => <Component {...props} />} />;
}

const App = () => {
  return (
    <div className="App">
      <AuthProvider>
      <LocaleProvider>
      <KioskModeProvider>
      <Router>
        <Switch>
          <PublicRoute path="/test" component={Test} exact />
          <PublicRoute path="/loading" component={Loading} exact />
          <PublicRoute path="/register" component={Register} exact />
          <PublicRoute path="/login" component={Login} exact />
          <PublicRoute
            path="/reset-password"
            component={ChangePassword}
            exact
          />
          <PublicRoute
            path="/forgot-password"
            component={ForgotPassword}
            exact
          />
          <PublicRoute path={"/verify"} component={VerifyTokenCode} />
          {/* <Route path={"verify-code} component={VerifyTokenCode} /> */}
          <PrivateRoute path="/">
            <MainLayout>
                <Route path="/dashboard" render={(props) => <Dashboard />} />
                <Route
                  path="/devices"
                  component={(props) => <Groups />}
                  exact
                />
                <Route
                  path="/devices/:deviceId"
                  component={DeviceDetails}
                  exact
                />
                <Route path="/groups/:groupId" component={GroupDetails} exact />
                <Route path="/devices/:deviceId/logs" component={Logs} />
                <Route path="/devices/:deviceId/general" component={General} />
                <Route path="/devices/:deviceId/manual" component={Manual} />
                <Route path="/devices/:deviceId/program" component={Program} />
                <Route
                  path="/devices/:deviceId/settings"
                  component={Settings}
                />
                <Route path="/devices/:deviceId/control" component={Control} />
                <Route path="/devices/:deviceId/stream" component={Stream} />
                <Route path="/devices/:deviceId/stereo" component={StereoStream} exact />
                <Route path="/devices/:deviceId/stereo-diagnostics" component={StereoDiagnostics} />
                <Route path="/devices/:deviceId/chat" component={DeviceChat} />
                {/* <Route path="/develop" render={(props) => <Develop />}></Route> */}

                {/* Develop routes */}
                <Route exact path="/develop" component={Develop} />
                <Route path="/develop/programs" component={Programs} />
                <Route
                  path="/develop/program/:id"
                  component={ProgramEditor}
                  exact
                />
                <Route path="/develop/actions" component={Actions} />
                <Route
                  path="/develop/action/:id"
                  component={ActionEditor}
                  exact
                />
                <Route path="/develop/config" component={DevelopConfig} />

                {/* DATA ROUTES */}
                <Route path="/data" render={(props) => <Data />} exact />
                <Route path="/data/datasets" component={Datasets} exact />
                <Route path="/data/datasets/:id" component={Dataset} exact />
                <Route path="/data/models" component={Models} exact />
                <Route path="/data/models/:id" component={Model} exact />
                <Route path="/data/workers" component={Workers} exact />
                <Route path="/data/config" component={DataConfig} exact />

                <Route
                  path="/marketplace"
                  render={(props) => <Marketplace />}
                  exact
                />
                <Route path="/marketplace" component={Marketplace} />
                <Route
                  path="/marketplace/block-libraries"
                  component={BlockLibraries}
                  exact
                />
                <Route
                  path={"/marketplace/block-libraries/:id"}
                  component={BlockLibrary}
                />
                <Route path="/marketplace/devices" component={Devices} exact />

                <Route path="/setting" component={SettingsMainPage} />
                <Route path="/settings" component={SettingsMain} />
                <Route path="/profile" component={UserProfile} />
                <Route path="/notifications" component={Notifications} />
                <Route path="/support" component={Support} />

                <Route path="/organization" component={OrganizationAdmin} exact />

                {/* MES routes */}
                <Route exact path="/mes" component={MES} />
                <Route exact path="/mes/planning" component={Planning} />
                <Route path="/mes/starter-plan" component={StarterPlan} />
                <Route path="/mes/staff" component={StaffAllocation} />
                <Route exact path="/mes/monitoring" component={MESMonitoringDashboard} />
                <Route path="/mes/kpi" component={KPIDashboard} />
                <Route exact path="/mes/work-orders" component={WorkOrders} />
                <Route path="/mes/production-work-orders" component={ProductionWorkOrders} />
                <Route path="/mes/work-orders-tablet" component={TabletWorkOrders} />
                <Route path="/mes/process-data" component={ProcessData} />
                <Switch>
                  <Route exact path="/mes/trials/new" component={TrialCreatePage} />
                  <Route exact path="/mes/trials" component={Trials} />
                  <Route path="/mes/trials/:trialId" component={TrialDetailPage} />
                </Switch>
                <Route path="/mes/saved-trials" component={SavedTrials} />
                <Route path="/mes/trial-tablet" component={TrialTablet} />
                <Route path="/mes/trial-dashboard" component={TrialDashboard} />
                <Route exact path="/mes/labos/mjerenja" component={LabosMeasurements} />
                <Route exact path="/mes/labos/nalozi" component={LabosNalozi} />
                <Route path="/mes/labos" render={() => <Redirect to="/mes/labos/mjerenja" />} />
                <Route path="/mes/flour-analyses/lot/:lotId" component={FlourQualityLotPage} />
                <Route path="/mes/flour-analyses/history" component={FlourQualityHistory} />
                <Route exact path="/mes/flour-analyses" component={FlourQualityQueue} />
                <Route exact path="/mes/ingredient-prices" component={IngredientPrices} />
                <Route path="/mes/product-costs" component={ProductCosts} />
                <Route path="/mes/products" component={Products} />
                <Route path="/mes/recipes" component={Recipes} />
                <Route path="/mes/machines" component={Machines} />
                <Route path="/mes/ingredients" component={Ingredients} />
                <Route path="/mes/order-requests" component={OrderRequests} />
                <Route path="/mes/semi-finished" component={SemiFinished} />
                <Route path="/mes/receive-delivery" component={ReceiveDelivery} />
                <Route path="/mes/primke" component={ReceiveDelivery} />
                <Route path="/mes/inter-warehouse-transfers" component={InterWarehouseTransfers} />
                <Route path="/mes/stock-by-warehouse" component={StockByWarehouse} />
                <Route path="/mes/inventura" component={Inventura} />
                <Route path="/mes/finished-goods" component={FinishedGoods} />
                <Route path="/mes/stock-overview" component={StockOverview} />
                <Route path="/mes/traceability" component={Traceability} />
                <Route path="/mes/dispatch" component={DispatchNotes} />
                <Route path="/mes/overview" component={RegistryOverview} />
                <Route path="/mes/reports" component={DailyReport} />
                <Route exact path="/mes/batches" component={Batches} />
                <Route path="/mes/batches/:batchId" component={BatchDetail} />
                <Route path="/mes/tablet/mixer/:mixer?" component={TabletMixer} />
                <Route path="/mes/tablet/processing/:machine?" component={TabletProcessing} />
                <Route path="/mes/tablet/oven/:oven?" component={TabletOven} />
                <Route path="/mes/tablet/packaging" component={TabletPackaging} />

                <Status />
            </MainLayout>
          </PrivateRoute>
        </Switch>
      </Router>
      </KioskModeProvider>
      </LocaleProvider>
      </AuthProvider>
    </div>
  );
};

export default App;
